import { useLazyQuery, useQuery } from '@apollo/client/react';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { v4 as uuid } from 'uuid';
import { GET_ALL_CREWS, GET_ALL_ACCESSORIALS, GET_LOAD_SUMMARY } from '../../graphql/client/queries';
import {
    GetAllAccessorials,
    GetAllAccessorials_getAllAccessorials as AccessorialType,
    GetAllCrews,
    GetAllCrews_getAllCrews as CrewType,
    GetAllRegions_getAllRegions as RegionType,
    GetLoadSummary,
    GetLoadSummary_getLoadSummary_loadSummaries as LoadSummaryDetails,
} from '../../types/schemaTypes';
import { CustomButton, DateTimePicker } from '../_common';
import SearchableDropdown from '../_common/SearchableDropdown/SearchableDropdown';
import { Textfield } from '../_common/Textfield/Textfield';
import './EditJobForm.scss';
import useEditJobProfileForm from './useEditJobProfileForm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { geoFenceTypes, stagingGeoFenceTypes } from '../_common/Geofence/GeofenceTypes';
import { Modal } from 'react-bootstrap';
import GeofenceMap from '../_common/Geofence/GeofenceMap';
import Switch from "react-switch";
import { PermissionEnum } from '../../types/PermissionEnum';
import { useAuthContext } from '../Auth/AuthProvider';
import {  ToastContainer, toast } from 'react-toastify';
import {toastOpt} from '../../utils/helpers/toastHelper'

library.add(fas);

interface Option {
    id: number | string;
    name: string | number;
}

interface Suggestion {
    id: string | number;
    name: string;
}

interface LatLng {
    lat: number;
    lng: number;
}

let type;

const getRegionDistricts = (regionsList: RegionType[], regionId: number | string): Option[] => {
    const selectedRegion = regionsList.filter((item: RegionType) => item.id === regionId)[0];
    const districtsList: Option[] = [];
    if (selectedRegion && selectedRegion.districts) {
        selectedRegion.districts.forEach((element: any) => {
            if (element && element.districtName !== null) {
                districtsList.push({
                    id: element.id,
                    name: element.districtName,
                });
            }
        });
    }

    return districtsList;
};

const customerCrewText = 'This customer does not have any crew.';

const formatDate = (text: string | null): string => {
    if (text == null || text == '') return '';
    if (text.length == 0) return '';
    const dateParsed = new Date(Date.parse(text)).toDateString();
    const dateYYYYMMDD = moment(dateParsed).format('YYYY-MM-DD');
    if (dateYYYYMMDD == '1969-12-31') return '';
    const newDate = moment(dateYYYYMMDD).format('MMM DD, YYYY');
    return newDate;
};

const formatActualDate = (text: string | null): string => {
    if (text == null || text == '') return "";
    const dateParsed = new Date(Date.parse(text)).toDateString();
    const newDate = moment(dateParsed).format('MM/DD/YYYY');
    return newDate;
};

const EditJobForm = ({
    jobProfileData,
    regionsWithDistricts,
    statuses,
    customers,
    regions,
    epCompanies,
    states,
    crews,
    jobId,
}: any): React.ReactElement => {
    const [getCrewsByCustomer, { data: crewsData, loading: crewsLoading }] = useLazyQuery<GetAllCrews>(GET_ALL_CREWS);
    const [customerCrewError, setCustomerCrewError] = useState('');
    const [show, setShow] = useState(false);
    const [showStagingMap, setShowStagingMap] = useState(false);
    const [disableStaging, setDisableStaging] = useState(false);
    const {
        handleSubmit,
        handleDropdownChange,
        handleInputChange,
        handleAutoCompleteChange,
        handleDateInputChange,
        handleMapSave,
        inputs,
        fieldErrors,
        validateLatitude,
        validateLongitude,
        validateZipNumber,
        validatePhoneNumber,
        validateStartDate,
        validateEndDate,
        disable,
        handleInputChangeForRequiredFields,
        validateStagingLatitude,
        validateStagingLongitude,
        mutationError,
        setMutationError,
        setDisable,
    } = useEditJobProfileForm(jobProfileData, jobId);
    const { data: accessorialsData } = useQuery<GetAllAccessorials>(GET_ALL_ACCESSORIALS);
    const [path, setPath] = useState<LatLng[]>([]);
    const accessorials: Option[] = [];
    const isEditBellyDumpJobEnabled = useAuthContext().userHasPermission(PermissionEnum.EditBellyDumpJob);
    const [totalInProgressLoads, setTotalInProgressLoads] = useState(0);

    if (accessorialsData?.getAllAccessorials) {
        (accessorialsData.getAllAccessorials as (AccessorialType | null)[]).forEach((element: AccessorialType | null): void => {
            if (element != null && element.accessorialName != null) {
                accessorials.push({
                    id: element.accessorialId,
                    name: element.accessorialName,
                });
            }
        });
    }

    accessorials.push({ id: '0', name: 'None' });

    const [districts, setDistricts] = useState(getRegionDistricts(regionsWithDistricts, inputs.regionId));
    const [allCrews, setAllCrews] = useState(crews);
    const [stagingGeofenceLatitude, setStagingGeofenceLatitude] = useState(inputs.stagingLatitude ?? 0);
    const [stagingGeofenceLongitude, setStagingGeofenceLongitude] = useState(inputs.stagingLongitude ?? 0);
    const [stagingGeofencePolygonCoordinates, setStagingGeofencePolygonCoordinates] = useState(inputs.stagingPolygonCoordinates ?? []);
    const [stagingGeoFenceRadius, setStagingGeoFenceRadius] = useState(inputs.stagingGeoFenceRadius ?? 300);
    const [wellSiteLatitude, setWellSiteLatitude] = useState(inputs.latitude ?? 0);
    const [wellSiteLongitude, setWellSiteLongitude] = useState(inputs.longitude ?? 0);
    const [isBellyDump, setIsBellyDump] = useState(inputs.isBellyDump ?? false);

    const { data, loading } = useQuery<GetLoadSummary>(GET_LOAD_SUMMARY, {
        variables: { jobId: jobId },
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (data && data.getLoadSummary) {
            let totalLoads = 0;
            data.getLoadSummary.forEach((proppant) => {
                proppant?.loadSummaries?.forEach((item: LoadSummaryDetails | null) => {
                    if (item !== null && item.lofaName !== '') {
                        totalLoads += item.loadsInProgress || 0;
                    }
                });
            });
            setTotalInProgressLoads(totalLoads);
        }
    }, [data, loading]);


    useEffect(() => {
        if (crewsData?.getAllCrews && !crewsLoading) {
            if (crewsData.getAllCrews.length === 0 && inputs.customerName !== '') {
                setCustomerCrewError(customerCrewText);
            } else {
                setCustomerCrewError('');
                const newAllCrews: Suggestion[] = [];
                (crewsData.getAllCrews as (CrewType | null)[]).forEach((element: CrewType | null): void => {
                    if (element != null && element.crewNumber != null) {
                        newAllCrews.push({
                            id: element.id,
                            name: element.crewNumber,
                        });
                    }
                });
                setAllCrews(newAllCrews);
            }
        }
    }, [crewsData, crewsLoading]);

    const getCustomerCrews = (selectedCustomer: string | null): void => {
        if (selectedCustomer !== null) {
            getCrewsByCustomer({
                variables: {
                    customerId: +selectedCustomer,
                },
            });
        }
    };

    const onCancel = (): void => {
        setMutationError('');
        window.history.back();
    };

    const [regionName, setRegionName] = useState(inputs.regionName);
    const [districtName, setDistrictName] = useState(inputs.districtName);
    const [tabTitle, setTabTitle] = useState(inputs.wellName);
    document.title = inputs.wellName ? inputs.wellName : tabTitle;
    const onRegionChange = (regionId: any): void => {
        setDistricts(getRegionDistricts(regionsWithDistricts, +regionId));
        const selectedRegion = regionsWithDistricts.filter((item: RegionType) => item.id === +regionId)[0];
        if (selectedRegion) {
            setRegionName(selectedRegion.regionName);
            setDistrictName('');
        }
    };

    const onDistrictChange = (districtId: any): void => {
        if (districts.length > 0) {
            const selectedDistrict = districts.filter((item: any) => item.id === +districtId)[0];
            setDistrictName(selectedDistrict.name || '');
        }
    };

    const [wellSiteGeoFenceType, setWellSiteGeoFenceType] = useState(inputs.wellsiteGeoFenceType);

    const setInputLatLngForGeofenceStaging = () => {
        setStagingGeofenceLatitude(inputs.stagingLatitude);
        setStagingGeofenceLongitude(inputs.stagingLongitude);
    }
    const changeStagingGeoFenceType = (eventkey: any): void => {
        setDisable(false);
        if (eventkey == '') return;
        const numId = parseInt(eventkey);
        const filteredType = stagingGeoFenceTypes.filter((item: Option) => item.id === numId);
        const type = filteredType[0].name.toString();
        inputs.stagingGeoFenceType = type;
        setstagingGeoFenceType(type);

        switch (type) {
            case 'Circle':
                setDisableStaging(false);
                setInputLatLngForGeofenceStaging();
                setStagingGeoFenceRadius(300);
                fieldErrors.stagingGeoFenceRadiusError = '';
                break;
            case 'Polygon':
                setDisableStaging(false);
                setInputLatLngForGeofenceStaging();
                setStagingGeofencePolygonCoordinates(inputs.stagingPolygonCoordinates);
                setStagingGeoFenceRadius(0);
                fieldErrors.stagingGeoFenceRadiusError = '';
                break;
            case "None":
                setDisableStaging(true);
                setStagingGeoFenceRadius(0);
                setStagingGeofencePolygonCoordinates([]);
                fieldErrors.stagingGeoFenceRadiusError = '';
                break;
        }
    };

    const [stagingGeoFenceType, setstagingGeoFenceType] = useState(inputs.stagingGeoFenceType);

    const changeGeoFenceType = (eventkey: any): void => {
        setDisable(false);
        if (eventkey == '') return;
        const numId = parseInt(eventkey);
        const filteredType = geoFenceTypes.filter((item: Option) => item.id === numId);
        const type = filteredType[0].name.toString();
        inputs.wellsiteGeoFenceType = type;
        setWellSiteGeoFenceType(type);
        if (type == 'Circle') {
            inputs.wellsiteGeoFenceRadius = 300;
        }
        else {
            inputs.wellsiteGeoFenceRadius = 0;
            fieldErrors.wellSiteRadiusError = '';
        }
    };

    const handleDissmis = (): void => {
        handleMapSave();
        setShow(false);
        setShowStagingMap(false);
        setMutationError('');
        setIsBellyDump(false);
        console.log(inputs);
    };

    const handleOnWellSiteEdit = (polygonCoordinates: LatLng[]) => {
        inputs.wellsitePolygonCoordinates = polygonCoordinates;
    };

    const handleOnStagingEdit = (polygonCoordinates: LatLng[]) => {
        inputs.stagingPolygonCoordinates = polygonCoordinates;
    };

    const handleOnWellSiteCircleDrag = newCoords => {
        inputs.latitude = newCoords.lat();
        inputs.longitude = newCoords.lng();
    };

    const handleOnWellSiteRadiusEdit = (newRadius: number) => {
        inputs.wellsiteGeoFenceRadius = Math.round(newRadius);
    };

    const handleOnStagingCircleDrag = newCoords => {
        inputs.stagingLatitude = newCoords.lat();
        inputs.stagingLongitude = newCoords.lng();
    };

    const handleOnStagingRadiusEdit = (newRadius: number) => {
        inputs.stagingGeoFenceRadius = Math.round(newRadius);
    };

    const handleWellSiteLatLngChange = (lat: number, lng: number) => {
        inputs.latitude = lat;
        inputs.longitude = lng;
        setWellSiteLatitude(lat);
        setWellSiteLongitude(lng);
    }

    const handleStagingLatLngChange = (lat: number, lng:  number) => {
        inputs.stagingLatitude = lat;
        inputs.stagingLongitude = lng;
        setStagingGeofenceLatitude(lat);
        setStagingGeofenceLongitude(lng);
    }

    const handleToggleChange=(checked) =>{
        if(totalInProgressLoads > 0){
            toast.error('Cannot toggle job type back to normal until all loads in progress are delivered.',toastOpt);
            return;
        }
        setIsBellyDump(checked);
        inputs.isBellyDump = checked;
        setDisable(false);
    };

    const searchIcon = <FontAwesomeIcon icon={{ prefix: 'fas', iconName: 'search' }} />;

    return (
        <React.Fragment>
            <div className="EditJob-Container">
                <span className="EditJobNav-TabContainer">
                    <div className="JobProfileNav-Tab JobProfileNav-Selected" onClick={(): void => { }}>
                        <span>Job Profile</span>
                    </div>
                </span>
                <ToastContainer />

                <div className="EditJob-Container">
                    <div className="EditJob-Header">
                        <div className="EditJob-HeaderLabel">
                            <i className="material-icons EditJob-EditIcon">create</i>
                            <span className="EditJob-EditLabel-Bold">Edit</span>
                            <span className="EditJob-EditLabel">Job#: {jobProfileData.jobDetails.jobId}</span>
                        </div>
                        <span className="EditJob-Error">{mutationError}</span>

                        <div className="EditJob-HeaderButtonsContainer">
                            <CustomButton
                                isBordered
                                additionalStyle={'EditJob-HeaderButtonsAdditional'}
                                title="Cancel"
                                onClick={onCancel}
                            ></CustomButton>

                            <CustomButton
                                title="Save"
                                onClick={() => {
                                    setMutationError('');
                                    handleSubmit();
                                }}
                                isDisabled={disable}
                            ></CustomButton>
                        </div>
                    </div>
                    <div className="EditJob-DividerLine" />
                    <div className="EditJob-BodyContainer">
                        <Row>
                            <span className="EditJob-LabelText">Job Details</span>
                        </Row>
                        <br></br>
                        {isEditBellyDumpJobEnabled && <Row>
                                <label>
                                    <Switch
                                        checked={isBellyDump}
                                        onChange={handleToggleChange}
                                        onColor="#b6bd00"
                                        onHandleColor="#f6f6f6"
                                        handleDiameter={24}
                                        uncheckedIcon={false}
                                        checkedIcon={false}
                                        boxShadow="0px 1px 5px rgba(162, 193, 83, 0.6)"
                                        activeBoxShadow="0px 0px 1px 10px rgba(165, 205, 71, 0.2)"
                                        height={20}
                                        width={48}
                                        className="react-switch"
                                        id="material-switch"
                                    />
                                    <span className='AddJob-BTB-Toggle-Class'>Belly to box</span>
                                </label>
                            </Row>}
                        <Row>
                            <Col md={3}>
                                <Textfield
                                    label="JOB NAME"
                                    name="wellName"
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                    }}
                                    value={inputs.wellName}
                                    error={fieldErrors.wellNameError !== ''}
                                    helperText={fieldErrors.wellNameError}
                                    maxLength={40}
                                    required
                                />
                            </Col>
                            <Col md={3} className="EditJob-Region-Container">
                                <SearchableDropdown
                                    dropdownOptions={statuses}
                                    id="statusId"
                                    label="STATUS"
                                    onSelect={(eventKey: string | null) => {
                                        handleDropdownChange('statusId', eventKey);
                                    }}
                                    value={inputs.status}
                                />
                            </Col>
                            <Col md={3}>
                                <Textfield
                                    label="JDE #"
                                    value={inputs.jdeNumber}
                                    name="jdeNumber"
                                    onChange={event => {
                                        handleInputChange(event);
                                    }}
                                    error={fieldErrors.jdeNumberError !== ''}
                                    helperText={fieldErrors.jdeNumberError}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <DateTimePicker
                                    dateOnly
                                    label="*PLANNED START DATE"
                                    value={formatDate(inputs.startDate)}
                                    onChange={(value: any) => {
                                        handleDateInputChange('startDate', value);
                                        validateStartDate(value);
                                    }}
                                    error={fieldErrors.startDateError || ''}
                                />
                            </Col>
                            <Col md={3}>
                                <DateTimePicker
                                    dateOnly
                                    label="*PLANNED END DATE"
                                    value={formatDate(inputs.endDate)}
                                    onChange={(value: any) => {
                                        handleDateInputChange('endDate', value);
                                        validateEndDate(value);
                                    }}
                                    error={fieldErrors.endDateError || ''}
                                />
                            </Col>
                            <Col md={3}>
                                <div className="EditJob-Label">ACTUAL START DATE</div>
                                <input className="EditJob-InputBox" value={formatActualDate(jobProfileData.jobDetails.actualStartDate)} disabled />
                            </Col>
                            <Col md={3}>
                                <div className="EditJob-Label">ACTUAL END DATE</div>
                                <input className="EditJob-InputBox" value={formatActualDate(jobProfileData.jobDetails.actualEndDate)} disabled />
                            </Col>
                            <Col md={3}></Col>
                        </Row>
                        <Row>
                            <Col md={3} className="EditJob-Region-Container">
                                <SearchableDropdown
                                    dropdownOptions={regions}
                                    errorMessage={fieldErrors.regionError}
                                    hasError={fieldErrors.regionError !== ''}
                                    id="regionId"
                                    label="REGION"
                                    onSelect={(eventKey: string | null) => {
                                        handleDropdownChange('regionId', eventKey);
                                        onRegionChange(eventKey);
                                    }}
                                    required
                                    value={regionName}
                                />
                            </Col>
                            <Col md={3} className="EditJob-Region-Container">
                                <SearchableDropdown
                                    dropdownOptions={districts}
                                    errorMessage={fieldErrors.districtError}
                                    hasError={fieldErrors.districtError !== ''}
                                    id="districtId"
                                    label="DISTRICT"
                                    onSelect={(eventKey: string | null) => {
                                        handleDropdownChange('districtId', eventKey);
                                        onDistrictChange(eventKey);
                                    }}
                                    required
                                    value={districtName}
                                />
                            </Col>
                            <Col md={2}></Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Textfield
                                    label="ADDRESS 1"
                                    name="addressLine1"
                                    value={inputs.addressLine1}
                                    onChange={event => {
                                        handleInputChange(event);
                                    }}
                                />
                            </Col>
                            <Col md={4}>
                                <Textfield
                                    label="ADDRESS 2"
                                    name="addressLine2"
                                    value={inputs.addressLine2}
                                    onChange={event => {
                                        handleInputChange(event);
                                    }}
                                />
                            </Col>
                            <Col md={2}>
                                <Textfield
                                    label="CITY"
                                    name="city"
                                    value={inputs.city}
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                    }}
                                    error={fieldErrors.cityError !== ''}
                                    helperText={fieldErrors.cityError}
                                    required
                                />
                            </Col>
                            <Col md={1}>
                                <Textfield
                                    label="ZIP"
                                    name="zip"
                                    value={inputs.zip}
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                        validateZipNumber(event.target.value);
                                    }}
                                    maxLength={5}
                                    error={fieldErrors.zipError !== ''}
                                    helperText={fieldErrors.zipError}
                                    required
                                />
                            </Col>
                            <Col md={1}>
                                <SearchableDropdown
                                    autoCompleteEnabled={true}
                                    dropdownOptions={states}
                                    errorMessage={fieldErrors.stateError}
                                    hasError={fieldErrors.stateError !== ''}
                                    id="stateName"
                                    label="STATE"
                                    onSelect={(eventKey: string | null) => {
                                        handleAutoCompleteChange('stateName', {
                                            id: eventKey,
                                            name: inputs.states,
                                        });
                                    }}
                                    required
                                    value={inputs.stateName}
                                    endIcon={searchIcon}
                                />
                            </Col>
                        </Row>
                        <br />
                        <Row>
                            <span className="EditJob-LabelText">Wellsite Geofence</span>
                        </Row>
                        <Row>
                            <Col md={2}>
                                <Textfield
                                    label="LATITUDE"
                                    name="latitude"
                                    value={parseFloat(wellSiteLatitude).toFixed(6)}
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                        validateLatitude(event.target.value);
                                    }}
                                    error={fieldErrors.latitudeError !== ''}
                                    helperText={fieldErrors.latitudeError}
                                    type="number"
                                    min="-90"
                                    max="90"
                                    step="0.1"
                                    required
                                />
                            </Col>
                            <Col md={2}>
                                <Textfield
                                    label="LONGITUDE"
                                    name="longitude"
                                    value={parseFloat(wellSiteLongitude).toFixed(6)}
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                        validateLongitude(event.target.value);
                                    }}
                                    type="number"
                                    min="-180"
                                    max="180"
                                    step="0.1"
                                    error={fieldErrors.longitudeError !== ''}
                                    helperText={fieldErrors.longitudeError}
                                    required
                                />
                            </Col>
                            <Col md={2} className="EditJob-Region-Container">
                                <SearchableDropdown
                                    dropdownOptions={geoFenceTypes}
                                    errorMessage={fieldErrors.wellsiteGeofenceTypeError}
                                    hasError={fieldErrors.wellsiteGeofenceTypeError !== ''}
                                    id="GeofenceType"
                                    label="GEOFENCE TYPE"
                                    onSelect={(eventKey: string | null) => {
                                        // handleDropdownChange('wellsiteGeoFenceType', eventKey);
                                        changeGeoFenceType(eventKey);
                                    }}
                                    required
                                    value={wellSiteGeoFenceType}
                                />
                            </Col>
                            <Col md={2}>
                                {wellSiteGeoFenceType == 'Circle' && (
                                    <Textfield
                                        label="RADIUS (Meters)"
                                        name="wellsiteGeoFenceRadius"
                                        type="number"
                                        error={fieldErrors.wellsiteGeofenceRadiusError !== ''}
                                        helperText={fieldErrors.wellsiteGeofenceRadiusError}
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                        }}
                                        required={wellSiteGeoFenceType == 'Circle'}
                                        value={inputs.wellsiteGeoFenceRadius}
                                    />
                                )}
                            </Col>
                            <Col md={2} className="EditJob-ViewGeofence-Button">
                                <span
                                    className={
                                        inputs.latitude === 0 ||
                                            inputs.latitude.toString() === '' ||
                                            inputs.longitude === 0 ||
                                            inputs.longitude.toString() === ''
                                            ? 'disabled-viewGeofence'
                                            : 'Geofence-ViewLink'
                                    }
                                    onClick={() => setShow(true)}
                                >
                                    View Geofence
                                </span>
                            </Col>
                            <Col md={2}></Col>
                        </Row>
                        <br />
                        <Row>
                            <span className="EditJob-LabelText">Staging Geofence</span>
                        </Row>
                        <Row>
                            <Col md={2}>
                                <Textfield
                                    label="LATITUDE"
                                    name="stagingLatitude"
                                    value={parseFloat(stagingGeofenceLatitude).toFixed(6)}
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                        validateStagingLatitude(event.target.value);

                                        if (event.target.value === "" && inputs.stagingLongitude === "") {
                                            inputs.stagingGeoFenceType = '';
                                            setstagingGeoFenceType('');
                                            inputs.stagingGeoFenceRadius = 0;
                                        } else if (stagingGeoFenceType === '') {
                                            inputs.stagingGeoFenceType = 'Circle';
                                            setstagingGeoFenceType('Circle');
                                            inputs.stagingGeoFenceRadius = 300;
                                        }
                                    }}
                                    error={fieldErrors.stagingLatitudeError !== ''}
                                    helperText={fieldErrors.stagingLatitudeError}
                                    type="number"
                                    min="-90"
                                    max="90"
                                    step="0.1"
                                    disabled={disableStaging || inputs.stagingGeoFenceType == 'None'}
                                />
                            </Col>
                            <Col md={2}>
                                <Textfield
                                    label="LONGITUDE"
                                    name="stagingLongitude"
                                    value={parseFloat(stagingGeofenceLongitude).toFixed(6)}
                                    onChange={event => {
                                        handleInputChangeForRequiredFields(event);
                                        validateStagingLongitude(event.target.value);

                                        if (event.target.value === "" && inputs.stagingLatitude === "") {
                                            inputs.stagingGeoFenceType = '';
                                            setstagingGeoFenceType('');
                                            inputs.stagingGeoFenceRadius = 0;
                                        } else if (stagingGeoFenceType === '') {
                                            inputs.stagingGeoFenceType = 'Circle';
                                            setstagingGeoFenceType('Circle');
                                            inputs.stagingGeoFenceRadius = 300;
                                        }
                                    }}
                                    type="number"
                                    min="-180"
                                    max="180"
                                    step="0.1"
                                    error={fieldErrors.stagingLongitudeError !== ''}
                                    helperText={fieldErrors.stagingLongitudeError}
                                    disabled={disableStaging || inputs.stagingGeoFenceType == 'None'}
                                />
                            </Col>
                            <Col md={2} className="EditJob-Region-Container">
                                <SearchableDropdown
                                    dropdownOptions={stagingGeoFenceTypes}
                                    errorMessage={fieldErrors.stagingGeofenceTypeError}
                                    hasError={fieldErrors.stagingGeofenceTypeError !== ''}
                                    id="GeofenceType"
                                    label="GEOFENCE TYPE"
                                    onSelect={(eventKey: string | null) => {
                                        handleDropdownChange('stagingGeoFenceType', eventKey);
                                        changeStagingGeoFenceType(eventKey);
                                    }}
                                    required
                                    value={stagingGeoFenceType}
                                />
                            </Col>
                            <Col md={2}>
                                {stagingGeoFenceType == 'Circle' && (
                                    <Textfield
                                        label="RADIUS (Meters)"
                                        name="stagingGeoFenceRadius"
                                        type="number"
                                        error={fieldErrors.stagingGeofenceRadiusError !== ''}
                                        helperText={fieldErrors.stagingGeofenceRadiusError}
                                        onChange={(event: any) => {
                                            handleInputChange(event);
                                        }}
                                        required
                                        value={stagingGeoFenceRadius}
                                    />
                                )}
                            </Col>
                            <Col md={2} className="EditJob-ViewGeofence-Button">
                                <span
                                    className={
                                        inputs.stagingLatitude === 0 ||
                                            inputs.stagingLatitude.toString() === '' ||
                                            inputs.stagingLongitude === 0 ||
                                            inputs.stagingLongitude.toString() === '' ||
                                            disableStaging ||
                                            inputs.stagingGeoFenceType == 'None'
                                            ? 'disabled-viewGeofence'
                                            : 'Geofence-ViewLink'
                                    }
                                    onClick={() => setShowStagingMap(true)}
                                >
                                    View Geofence
                                </span>
                            </Col>
                            <Col md={2}></Col>
                        </Row>
                        <div className="EditJob-NotesContainer">
                            <div className="EditJob-NotesLabel">DIRECTIONS</div>
                            <textarea
                                className="EditJob-NotesParagraph"
                                name="directions"
                                rows={3}
                                value={inputs.directions}
                                onChange={event => {
                                    handleInputChange(event);
                                }}
                            ></textarea>
                        </div>
                        <Row>
                            <span className="EditJob-LabelText">Customer Details</span>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <SearchableDropdown
                                    autoCompleteEnabled={true}
                                    dropdownOptions={customers}
                                    errorMessage={fieldErrors.customerError || customerCrewError}
                                    hasError={fieldErrors.customerError !== '' || customerCrewError !== ''}
                                    id="customerName"
                                    label="CUSTOMER"
                                    onSelect={(eventKey: string | null) => {
                                        handleAutoCompleteChange('customerName', {
                                            id: eventKey,
                                            name: inputs.customerName,
                                        });
                                        getCustomerCrews(eventKey);
                                    }}
                                    required
                                    value={inputs.customerName}
                                    endIcon={searchIcon}
                                />
                            </Col>
                            <Col md={4}>
                                <SearchableDropdown
                                    autoCompleteEnabled={true}
                                    dropdownOptions={epCompanies}
                                    errorMessage={fieldErrors.epCompanyError}
                                    hasError={fieldErrors.epCompanyError !== ''}
                                    id="epCompany"
                                    label="E&P COMPANY"
                                    onSelect={(eventKey: string | null) => {
                                        handleAutoCompleteChange('epCompany', {
                                            id: eventKey,
                                            name: inputs.epCompany,
                                        });
                                    }}
                                    value={inputs.epCompany}
                                    endIcon={searchIcon}
                                />
                            </Col>
                            <Col md={3}>
                                <SearchableDropdown
                                    autoCompleteEnabled={true}
                                    dropdownOptions={allCrews}
                                    errorMessage={fieldErrors.crewError}
                                    hasError={fieldErrors.crewError !== ''}
                                    id="crew"
                                    label="CREW"
                                    onSelect={(eventKey: string | null) => {
                                        handleAutoCompleteChange('crew', {
                                            id: eventKey,
                                        });
                                    }}
                                    required
                                    value={inputs.crewName}
                                    endIcon={searchIcon}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={3}>
                                <Textfield
                                    label="CONTACT"
                                    name="contact"
                                    value={inputs.contact}
                                    onChange={event => {
                                        handleInputChange(event);
                                    }}
                                />
                            </Col>
                            <Col md={2}>
                                <Textfield
                                    id={uuid()}
                                    label="PHONE NUMBER"
                                    name="phoneNumber"
                                    value={inputs.phoneNumber}
                                    onChange={event => {
                                        handleInputChange(event);
                                        validatePhoneNumber(event.target.value);
                                    }}
                                    maxLength={10}
                                    error={fieldErrors.phoneNumberError !== ''}
                                    helperText={fieldErrors.phoneNumberError}
                                />
                            </Col>
                        </Row>
                        <div className="EditJob-Accessorials">Accessorial</div>
                        <Row>
                            <Col md={3} className="AddJob-Region-Container">
                                <SearchableDropdown
                                    dropdownOptions={accessorials}
                                    id="accessorial"
                                    label="SELECT ACCESSORIAL TO INCLUDE"
                                    onSelect={(eventKey: string | null) => {
                                        handleDropdownChange('accessorial', eventKey);
                                    }}
                                    value={inputs.accessorialName}
                                />
                            </Col>

                            {inputs.accessorial !== 0 && (
                                <Col md={3} className="MilesForAccessorials">
                                    <Textfield
                                        label="MILES"
                                        name="miles"
                                        value={inputs.miles}
                                        onChange={(event: any) => {
                                            handleInputChangeForRequiredFields(event);
                                        }}
                                        required
                                        error={fieldErrors.milesError !== ''}
                                        helperText={fieldErrors.milesError}
                                    />
                                </Col>
                            )}
                        </Row>
                    </div>
                </div>
            </div>
            <Modal show={show} onHide={handleDissmis} size="xl">
                <Modal.Header className="AddJob-Header">
                    <div className="AddJob-Icon-Container">
                        <div className="AddJob-Header-Buttons">
                            <CustomButton
                                additionalStyle={'AddJob-Header-Buttons-Additional'}
                                isBordered
                                title="Cancel"
                                onClick={handleDissmis}
                            ></CustomButton>
                            <CustomButton
                                title="Save"
                                isDisabled={false}
                                onClick={() => {
                                    handleDissmis();
                                }}
                            ></CustomButton>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <GeofenceMap
                        latitude={parseFloat(wellSiteLatitude)}
                        longitude={parseFloat(wellSiteLongitude)}
                        selectedPolygonCoordinates={
                            inputs.wellsitePolygonCoordinates.length > 2
                                ? inputs.wellsitePolygonCoordinates
                                : jobProfileData.jobDetails.wellsitePolygonCoordinates
                        }
                        radius={inputs.wellsiteGeoFenceRadius}
                        geofenceType={inputs.wellsiteGeoFenceType}
                        onEditMap={handleOnWellSiteEdit}
                        onCircleDrag={handleOnWellSiteCircleDrag}
                        onEditCircleRadius={handleOnWellSiteRadiusEdit}
                        onChangeWellSiteLatLng={handleWellSiteLatLngChange}
                    />
                </Modal.Body>
            </Modal>
            <Modal show={showStagingMap} onHide={handleDissmis} size="xl">
                <Modal.Header className="AddJob-Header">
                    <div className="AddJob-Icon-Container">
                        <div className="AddJob-Header-Buttons">
                            <CustomButton
                                additionalStyle={'AddJob-Header-Buttons-Additional'}
                                isBordered
                                title="Cancel"
                                onClick={handleDissmis}
                            ></CustomButton>
                            <CustomButton
                                title="Save"
                                isDisabled={false}
                                onClick={() => {
                                    handleDissmis();
                                }}
                            ></CustomButton>
                        </div>
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <GeofenceMap
                        latitude={parseFloat(inputs.stagingLatitude)}
                        longitude={parseFloat(inputs.stagingLongitude)}
                        selectedPolygonCoordinates={
                            inputs.stagingPolygonCoordinates.length > 2
                                ? inputs.stagingPolygonCoordinates
                                : jobProfileData.jobDetails.stagingPolygonCoordinates
                        }
                        radius={inputs.stagingGeoFenceRadius}
                        geofenceType={inputs.stagingGeoFenceType}
                        onEditMap={handleOnStagingEdit}
                        onCircleDrag={handleOnStagingCircleDrag}
                        onEditCircleRadius={handleOnStagingRadiusEdit}
                        onChangeStagingLatLng={handleStagingLatLngChange}
                    />
                </Modal.Body>
            </Modal>
        </React.Fragment>
    );
};

export default EditJobForm;

